/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UsersRound, RefreshCw, Loader2, Search, AlertTriangle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { err } from "../components/finance/OutflowDialog";
import { PayeeLedgerTable, money } from "../components/finance/PayeeLedgerTable";

const SORTS = [
  { k: "paid", zh: "已付合计 高→低", en: "Paid high→low" },
  { k: "projects", zh: "项目数 多→少", en: "Projects" },
  { k: "inflight", zh: "在途 高→低", en: "In flight" },
  { k: "dups", zh: "疑似重复 多→少", en: "Duplicates" },
  { k: "recent", zh: "最近付款 新→旧", en: "Most recent" },
  { k: "name", zh: "名称 A→Z", en: "Name A→Z" },
];
const sorter = (k) => (a, b) => ({
  paid: () => (b.paidTotal + b.inflightTotal) - (a.paidTotal + a.inflightTotal),
  projects: () => b.projects - a.projects || b.paidTotal - a.paidTotal,
  inflight: () => b.inflightTotal - a.inflightTotal || b.paidTotal - a.paidTotal,
  dups: () => b.dupPairs - a.dupPairs || b.paidTotal - a.paidTotal,
  recent: () => (b.lastPaidDate || "").localeCompare(a.lastPaidDate || ""),
  name: () => (a.payee || "").localeCompare(b.payee || ""),
}[k]());

/* 公司级收款方总账（只读）：跨项目同一收款方一行（分包商库公司 ID / 名称归一化，与项目「按收款方汇总」同口径）→ 展开看每个项目 → 跳项目财务 */
export default function PayeeLedger() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branch, setBranch] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("paid");
  const [only, setOnly] = useState("");   // "" | dups | inflight | over | lien
  const load = useCallback((fresh) => {
    setLoading(true);
    return api.get(`/finance/payees?branch=${branch}${fresh ? "&fresh=1" : ""}`).then(({ data }) => setD(data)).catch((e) => toast.error(err(e))).finally(() => setLoading(false));
  }, [api, branch]);
  useEffect(() => { load(false); }, [load]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d?.rows || []).filter((r) => !s || (r.payee || "").toLowerCase().includes(s) || r.trades.some((t) => (t || "").toLowerCase().includes(s)) || r.perProject.some((p) => (p.address || "").toLowerCase().includes(s)))
      .filter((r) => !only || (only === "dups" ? r.dupPairs > 0 : only === "inflight" ? r.inflightCount > 0 : only === "over" ? r.over : r.lienPending > 0))
      .sort(sorter(sort));
  }, [d, q, sort, only]);
  const t = d?.totals || {};
  const sel = "text-xs rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-slate-600 focus:border-indigo-400 outline-none";
  const kpi = (id, label, value, cls = "text-slate-900", extra) => (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 min-w-0 sm:min-w-[150px]" data-testid={`payees-kpi-${id}`} data-value={extra ?? value}>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`text-lg font-bold ${cls}`}>{value}</div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="payee-ledger-page" data-count={rows.length} data-total={d?.rows?.length ?? ""}>
      <div className="flex flex-wrap items-center gap-2">
        <UsersRound className="h-5 w-5 text-red-600" />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{zh ? "收款方总账" : "Payee Ledger"}</h1>
        <span className="text-xs text-slate-400">{zh ? "跨项目同一收款方 · 只读 · 与项目财务「按收款方汇总」同口径" : "Same payee across all projects · read-only · same rules as project “By payee”"}</span>
        <button type="button" onClick={() => load(true)} disabled={loading} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="payees-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{zh ? "实时重算" : "Recompute"}
          {d && <span className="text-slate-400">· {d.cached ? (zh ? "缓存" : "cached") : (zh ? "实时" : "live")} {new Date(d.computedAt).toLocaleTimeString()}</span>}
        </button>
      </div>
      {!d ? <div className="h-40 animate-pulse bg-slate-50 rounded-xl" data-testid="payees-loading" /> : (<>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3" data-testid="payees-kpis">
          {kpi("payees", zh ? "收款方" : "Payees", t.payees)}
          {kpi("projects", zh ? "涉及项目" : "Projects", t.projects)}
          {kpi("paid", zh ? "已付合计" : "Paid total", money(t.paidTotal), "text-red-600", t.paidTotal)}
          {kpi("inflight", zh ? "在途付款申请" : "In flight", money(t.inflightTotal), t.inflightTotal > 0 ? "text-violet-700" : "text-slate-400", t.inflightTotal)}
          {kpi("unlinked", zh ? "未关联付款期" : "Unlinked", money(t.unlinked), t.unlinked > 0 ? "text-amber-700" : "text-slate-400", t.unlinked)}
          {kpi("dups", zh ? "疑似重复组" : "Possible duplicates", t.dupPairs, t.dupPairs > 0 ? "text-amber-700" : "text-slate-400")}
          {kpi("lien", zh ? "Lien Waiver 待签 / 已签" : "Lien pending / signed", `${t.lienPending} / ${t.lienSigned}`, t.lienPending > 0 ? "text-amber-700" : "text-slate-700", t.lienPending)}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="payees-filter">
            <div className="relative"><Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-2" /><input data-testid="payees-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? "搜收款方 / 工种 / 项目地址" : "Search payee / trade / address"} className={`${sel} pl-7 w-60`} /></div>
            {(d.branches || []).length > 1 && (
              <select data-testid="payees-branch" value={branch} onChange={(e) => setBranch(e.target.value)} className={sel}>
                <option value="all">{zh ? "全部账套" : "All entities"}</option>
                {d.branches.map((b) => <option key={b.key} value={b.key}>{zh ? b.zh : b.en}</option>)}
              </select>
            )}
            <select data-testid="payees-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={sel}>{SORTS.map((s) => <option key={s.k} value={s.k}>{zh ? s.zh : s.en}</option>)}</select>
            <div className="inline-flex flex-wrap max-w-full rounded-lg border border-slate-200 overflow-hidden text-xs" data-testid="payees-only" data-only={only}>
              {[["", zh ? "全部" : "All"], ["dups", zh ? "有疑似重复" : "Duplicates"], ["inflight", zh ? "有在途" : "In flight"], ["over", zh ? "超合同额" : "Over contract"], ["lien", zh ? "Lien 待签" : "Lien pending"]].map(([k, l], i) => (
                <button key={k} type="button" data-testid={`payees-only-${k || "all"}`} onClick={() => setOnly(k)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2.5 py-1 ${i ? "border-l border-slate-200" : ""} ${only === k ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            <span className="ml-auto text-[11px] text-slate-400" data-testid="payees-shown">{zh ? `显示 ${rows.length} / ${d.rows.length} · 扫描 ${d.projectsScanned} 个项目` : `${rows.length} / ${d.rows.length} shown · ${d.projectsScanned} projects scanned`}</span>
          </div>
          {t.dupPairs > 0 && <p className="mb-2 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="payees-dup-hint"><AlertTriangle className="h-3 w-3" />{zh ? "疑似重复 = 同收款方金额相同（±$1）且期次相同 / 对方未关联付款期；核对请到项目财务「按收款方汇总」。" : "Duplicate = same payee, same amount (±$1) and same installment / unlinked; review inside project finance “By payee”."}</p>}
          <PayeeLedgerTable rows={rows} zh={zh} />
        </div>
      </>)}
    </div>
  );
}
